import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiUser, FiMail, FiCalendar, FiEdit3, FiSave, FiX } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';

const ProfileContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 3rem;
  padding: 2rem;
  background: var(--background-secondary);
  border-radius: 0;
  border: 1px solid var(--border-color);
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 1.5rem;
    padding: 1.5rem;
    margin-bottom: 2rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    gap: 1rem;
  }
`;

const AvatarSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const Avatar = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background-color: var(--primary-color);
  color: var(--text-inverse);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  font-weight: 600;
  border: none;
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    width: 80px;
    height: 80px;
    font-size: 1.5rem;
  }
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled.h1`
  color: var(--text-primary);
  font-size: 2rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const UserRole = styled.div`
  display: inline-block;
  background: var(--primary-color);
  color: var(--text-inverse);
  padding: 0.25rem 0.75rem;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 1rem;
`;

const UserEmail = styled.div`
  color: var(--text-secondary);
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const JoinDate = styled.div`
  color: var(--text-muted);
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const ProfileContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  background: var(--background-secondary);
  border-radius: 0;
  padding: 2rem;
  border: 1px solid var(--border-color);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const SectionTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 1.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
    margin-bottom: 1.25rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 1rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    margin-bottom: 1.25rem;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 1rem;
  }
`;

const Label = styled.label`
  display: block;
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    margin-bottom: 0.375rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  font-family: 'Inter', sans-serif;
  border-radius: 0;
  background: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }

  &:disabled {
    background: var(--background-tertiary);
    color: var(--text-muted);
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 0.625rem;
    font-size: 0.8rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem;
    font-size: 0.75rem;
  }
`;

const Button = styled.button`
  background: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    background: var(--primary-hover);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  &.secondary {
    background: var(--background-tertiary);
    color: var(--text-primary);
    border: 1px solid var(--border-color);

    &:hover:not(:disabled) {
      background: var(--background-secondary);
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const StatCard = styled.div`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1rem;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--primary-color);
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

function Profile() {
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    bio: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stats, setStats] = useState({
    coursesEnrolled: 0,
    assignmentsCompleted: 0,
    averageGrade: 0,
    daysActive: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);

  const getInitials = (firstName, lastName) => {
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return first + last || 'U';
  };

  // Update form data when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        bio: ''
      });
    }
  }, [user]);

  // Fetch account statistics
  useEffect(() => {
    const fetchStats = async () => {
      if (!user) return;
      
      try {
        setStatsLoading(true);
        
        // Fetch dashboard stats using the existing API method
        const statsResponse = await apiService.getDashboardStats();
        
        if (statsResponse.success && statsResponse.stats) {
          // Calculate days active from user creation date
          const createdDate = new Date(user.created_at);
          const today = new Date();
          const daysActive = Math.floor((today - createdDate) / (1000 * 60 * 60 * 24));
          
          setStats({
            coursesEnrolled: statsResponse.stats.totalCourses || 0,
            assignmentsCompleted: statsResponse.stats.completedAssignments || 0,
            averageGrade: statsResponse.stats.averageGrade || 0,
            daysActive: daysActive || 0
          });
        }
      } catch (err) {
        console.error('Error fetching stats:', err);
        // Keep default values on error
      } finally {
        setStatsLoading(false);
      }
    };
    
    fetchStats();
  }, [user]);

  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Format phone number as user types
    let formattedValue = value;
    if (name === 'phone') {
      formattedValue = formatPhoneNumber(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));
    
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Strip phone formatting before sending to backend
      const phoneDigits = formData.phone ? formData.phone.replace(/\D/g, '') : '';
      
      // Validate phone if provided (must be exactly 10 digits or empty)
      if (phoneDigits && phoneDigits.length !== 10) {
        setError('Phone number must contain exactly 10 digits');
        setLoading(false);
        return;
      }
      
      const updateData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: phoneDigits || null
      };
      
      const response = await apiService.updateUserProfile(updateData);
      
      if (response.success) {
        setSuccess('Profile updated successfully!');
        setIsEditing(false);
        // Update user context with new data
        if (response.user) {
          updateUser(response.user);
        }
      } else {
        setError(response.error || 'Failed to update profile');
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: user?.first_name || '',
      lastName: user?.last_name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      bio: ''
    });
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  return (
    <ProfileContainer>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <ProfileHeader>
          <AvatarSection>
            <Avatar>
              {getInitials(user?.first_name, user?.last_name)}
            </Avatar>
          </AvatarSection>
          
          <UserInfo>
            <UserName>{user?.first_name} {user?.last_name}</UserName>
            <UserRole>{user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}</UserRole>
            <UserEmail>
              <FiMail size={16} />
              {user?.email}
            </UserEmail>
            <JoinDate>
              <FiCalendar size={16} />
              Member since {new Date(user?.created_at).toLocaleDateString()}
            </JoinDate>
          </UserInfo>
        </ProfileHeader>

        <ProfileContent>
          <Section>
            <SectionTitle>
              <FiUser size={20} />
              Personal Information
            </SectionTitle>
            
            <FormGroup>
              <Label>First Name</Label>
              <Input
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Last Name</Label>
              <Input
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Email</Label>
              <Input
                name="email"
                value={formData.email}
                onChange={handleChange}
                disabled={true}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Phone Number</Label>
              <Input
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="(xxx) xxx-xxxx"
                maxLength={14}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Bio</Label>
              <Input
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="Tell us about yourself..."
              />
            </FormGroup>
            
            {error && (
              <div style={{ color: 'var(--error-color)', marginTop: '0.5rem', fontSize: '0.875rem' }}>
                {error}
              </div>
            )}
            
            {success && (
              <div style={{ color: 'var(--success-color)', marginTop: '0.5rem', fontSize: '0.875rem' }}>
                {success}
              </div>
            )}

            <ButtonGroup>
              {isEditing ? (
                <>
                  <Button onClick={handleSave} disabled={loading}>
                    <FiSave size={16} />
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button className="secondary" onClick={handleCancel} disabled={loading}>
                    <FiX size={16} />
                    Cancel
                  </Button>
                </>
              ) : (
                <Button onClick={() => setIsEditing(true)}>
                  <FiEdit3 size={16} />
                  Edit Profile
                </Button>
              )}
            </ButtonGroup>
          </Section>

          <Section>
            <SectionTitle>
              <FiUser size={20} />
              Account Statistics
            </SectionTitle>
            
            <StatsGrid>
              <StatCard>
                <StatValue>{statsLoading ? '...' : stats.coursesEnrolled}</StatValue>
                <StatLabel>Courses Enrolled</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{statsLoading ? '...' : stats.assignmentsCompleted}</StatValue>
                <StatLabel>Assignments Completed</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{statsLoading ? '...' : stats.averageGrade > 0 ? `${stats.averageGrade}%` : 'N/A'}</StatValue>
                <StatLabel>Average Grade</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{statsLoading ? '...' : stats.daysActive}</StatValue>
                <StatLabel>Days Active</StatLabel>
              </StatCard>
            </StatsGrid>
          </Section>
        </ProfileContent>
      </motion.div>
    </ProfileContainer>
  );
}

export default Profile;
